/* Sham Social Proof - JS Logic */

class ShamSocialProof extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.scrollContainer = this.querySelector('[data-sham-scroll-area]');
        this.prevBtn = this.querySelector('[data-sham-prev]');
        this.nextBtn = this.querySelector('[data-sham-next]');
        this.cards = this.querySelectorAll('.sham-card');

        this.initObserver();
        this.initScroll();
        this.bindEvents();
    }

    initScroll() {
        // Resize observer to update nav state on window resize
        const resizeObserver = new ResizeObserver(() => this.updateNavState());
        if (this.scrollContainer) {
            resizeObserver.observe(this.scrollContainer);
            this.scrollContainer.addEventListener('scroll', () => this.updateNavState(), { passive: true });
            // Initial check
            this.updateNavState();
        }
    }

    scroll(direction) {
        if (!this.scrollContainer) return;

        const cardWidth = this.getCardWidth();
        const gap = 24; // var(--sham-space-6)
        const scrollAmount = cardWidth + gap;

        const currentScroll = this.scrollContainer.scrollLeft;
        const targetScroll = direction === 'next'
            ? currentScroll + scrollAmount
            : currentScroll - scrollAmount;

        this.scrollContainer.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
        });
    }

    getCardWidth() {
        if (this.cards.length > 0) {
            return this.cards[0].offsetWidth;
        }
        return 300;
    }

    updateNavState() {
        if (!this.scrollContainer || !this.prevBtn || !this.nextBtn) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;

        const isStart = scrollLeft <= 5;
        const isEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) <= 5;
        const isScrollable = scrollWidth > clientWidth;

        this.prevBtn.disabled = isStart || !isScrollable;
        this.nextBtn.disabled = isEnd || !isScrollable;
    }

    initObserver() {
        // Stricter Observer: Detects intersection with the exact vertical center line of the container
        // Margin: 0px -50% 0px -50% collapses the detection area to a thin center line

        const options = {
            root: this.scrollContainer,
            rootMargin: '0px -50% 0px -50%',
            threshold: 0
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.selectCard(entry.target);
                }
            });
        }, options);

        this.cards.forEach(card => this.observer.observe(card));
    }

    selectCard(targetCard) {
        // Deselect all others to ensure only one is active
        this.cards.forEach(card => {
            if (card === targetCard) {
                this.activateCard(card);
            } else {
                this.deactivateCard(card);
            }
        });
    }

    activateCard(card) {
        if (card.classList.contains('is-selected')) return;

        card.classList.add('is-selected');
        const video = card.querySelector('video');
        if (video) {
            // Video Play Logic: Resume if previously played
            video.play().catch(e => console.log('Autoplay prevented', e));
        }
    }

    deactivateCard(card) {
        if (!card.classList.contains('is-selected')) return;

        card.classList.remove('is-selected');
        const video = card.querySelector('video');
        if (video) {
            video.pause();
        }
    }

    bindEvents() {
        // Navigation clicks
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scroll('prev'));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scroll('next'));

        // Mute Toggle Logic
        this.cards.forEach(card => {
            const toggle = card.querySelector('.sham-mute-toggle');
            const video = card.querySelector('video');

            if (toggle && video) {
                toggle.addEventListener('click', (e) => {
                    e.stopPropagation(); // Mute toggle shouldn't trigger card clicks
                    video.muted = !video.muted;
                    this.updateMuteIcon(toggle, video.muted);
                });
            }
        });
    }

    updateMuteIcon(btn, isMuted) {
        if (isMuted) {
            // Show Muted Icon (Speaker X)
            btn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                </svg>`;
        } else {
            // Show Sound Icon (Speaker Wave)
            btn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                </svg>`;
        }
    }
}

customElements.define('sham-social-proof', ShamSocialProof);
